import { Group, InputWrapper, NumberInput, Select } from '@mantine/core';
import { useCreateRuleFormContext } from 'context/FormContext';
import { RuleTimeFrame, RuleTimeFrames, TimeFramesInSeconds } from 'models/rules';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isProduction } from 'utils/app';
import { secondsToTimeWindow } from 'utils/helpers';
import { displayFormError } from 'utils/validation';

interface Props {
  dataField: string;
  disabled?: boolean;
  allowedFrames?: RuleTimeFrame[];
}

export const TimeWindowField = ({ dataField, disabled, allowedFrames }: Props) => {
  const { t } = useTranslation();
  const { setFieldValue, values, getInputProps, errors } = useCreateRuleFormContext();
  const timeInSeconds = getInputProps(`${dataField}`).value;
  const fields = secondsToTimeWindow(timeInSeconds);
  const frames = allowedFrames ?? Object.values(RuleTimeFrames);
  const singleFrame = frames.length === 1;
  const [timeInput, setTimeInput] = useState<string | number>(fields.value || '');
  const [timeFrame, setTimeFrame] = useState<string | null>(fields.unit || frames[0] || RuleTimeFrames.hours);

  const handleChangeTime = (value: string | number) => {
    setTimeInput(value);
    const seconds = TimeFramesInSeconds[timeFrame as keyof typeof TimeFramesInSeconds] * Number(value);
    setFieldValue(dataField, seconds > 0 ? seconds : '');
  };

  const handleChangeTimeFrame = (value: string | null) => {
    setTimeFrame(value);
    if (value) {
      const seconds = TimeFramesInSeconds[value as keyof typeof TimeFramesInSeconds] * Number(timeInput);
      setFieldValue(dataField, seconds > 0 ? seconds : '');
    }
  };

  return (
    <InputWrapper label={t('time_window_label')}>
      <Group align={'flex-start'} gap={'xs'}>
        <NumberInput
          onChange={handleChangeTime}
          value={timeInput}
          hideControls
          allowNegative={false}
          allowDecimal={false}
          min={1}
          max={100}
          disabled={disabled || values.formDisabled}
          clampBehavior={'strict'}
          error={displayFormError({ errors, dataField })}
        />
        <Select
          value={timeFrame}
          onChange={handleChangeTimeFrame}
          data={frames
            .filter((frame) => !(isProduction && frame === RuleTimeFrames.minutes))
            .map((value) => ({
              value,
              label: t(`${value}_label`),
            }))}
          disabled={disabled || values.formDisabled || singleFrame}
        />
      </Group>
    </InputWrapper>
  );
};
